import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';

import {
  CustomEventTarget,
  matchObservableObjectChange,
  getObservableObjectKeyPath,
  getRelativeKeyPath,
  ObservableObjectChange,
  getIsObservableObject,
} from '@libs/util-ts';
import { ColorReference, FontReference, ImageReference, TextReference } from '../references';

import type { Composition } from '../compositions';
import type { VideoDescriptor, VideoDescriptorLayer } from '../index';
import { latest as LatestVDETypes } from '@libs/waymark-video/video-descriptor-types';
import { getFormattedColor, hexStringToRGBArray } from '../utils/colors';

// Inheritance with CustomEventTarget types gets a little funky if we have some base-level
// events which then can be extended by subclasses. We'll define a base event map here and
// all subclasses will need to include these events in their own event map to keep TypeScript
// from blowing up with all of the complex key extraction stuff that CustomEventTarget does.
export type BaseLayerEventMap = {
  change: void;
  'change:visibility': void;
  'change:fillEffectColor': void;
  'change:ignoredReferences': void;
  removed: void;
};

const NO_VALUE = Symbol('NO_VALUE');

export abstract class BaseLayer<
  TRawDataType extends LatestVDETypes.Layer,
  TEventMap extends BaseLayerEventMap = BaseLayerEventMap,
> extends CustomEventTarget<TEventMap> {
  uuid: string;
  layerType: TRawDataType['ty'];

  _rawLayerData: TRawDataType;
  parentComposition: Composition;
  // Keep a reference to the video descriptor instance; this is often needed for operations like looking up
  // a layer's asset.
  videoDescriptor: VideoDescriptor;

  imageContentReference: ImageReference | null = null;
  textContentReference: TextReference | null = null;
  fontReference: FontReference | null = null;
  fillColorReference: ColorReference | null = null;
  strokeColorReference: ColorReference | null = null;
  fillEffectColorReference: ColorReference | null = null;
  gradientFillColorReferences: ColorReference[] | null = null;
  // Track gradient fill color reference change listeners in an array for each gradient step so we can remove them later if needed
  gradientFillColorReferenceChangeListeners: Array<(event: CustomEvent<string>) => void> | null =
    null;

  // Tracks whether this layer instance has been detached from the video descriptor.
  isDetached = false;

  onImageContentReferenceChange?(): void;
  onTextContentReferenceChange?(): void;
  onFontReferenceChange?(): void;
  onFillColorReferenceChange?(): void;
  onStrokeColorReferenceChange?(): void;
  onGradientFillColorReferenceChange?(gradientStepIndex: number): void;

  onVideoDescriptorChange: (evt: CustomEvent<ObservableObjectChange>) => void;

  get rawLayerData() {
    return this._rawLayerData;
  }

  set rawLayerData(newRawLayerData: typeof this._rawLayerData) {
    if (getIsObservableObject(newRawLayerData)) {
      this._rawLayerData = newRawLayerData;
    } else {
      // If we're being assigned a non-observable object,
      // we should merge its values into the existing observable object
      // to avoid breaking our connection to the video descriptor.
      Object.assign(this._rawLayerData, newRawLayerData);
    }
  }

  /** Add overloads so that addEventListener can be called with common base layer events on unions of different layer types */
  addEventListener<TEventType extends keyof BaseLayerEventMap>(
    type: TEventType,
    listener: (event: CustomEvent<BaseLayerEventMap[TEventType]>) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  // Keep the original method for type-specific events
  addEventListener<TEventType extends Extract<keyof TEventMap, string>>(
    type: TEventType,
    listener: (event: CustomEvent<TEventMap[TEventType]>) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addEventListener<TEventType extends Extract<keyof TEventMap, string>>(
    type: TEventType,
    listener: (event: CustomEvent<TEventMap[TEventType]>) => void,
    options?: boolean | AddEventListenerOptions,
  ) {
    return super.addEventListener(type, listener, options);
  }

  /** Add overloads so that removeEventListener can be called with common base layer events on unions of different layer types */
  removeEventListener<TEventType extends keyof BaseLayerEventMap>(
    type: TEventType,
    listener: (event: CustomEvent<BaseLayerEventMap[TEventType]>) => void,
    options?: boolean | EventListenerOptions,
  ): void;
  removeEventListener<TEventType extends Extract<keyof TEventMap, string>>(
    type: TEventType,
    listener: (event: CustomEvent<TEventMap[TEventType]>) => void,
    options?: boolean | EventListenerOptions,
  ): void;
  removeEventListener<TEventType extends Extract<keyof TEventMap, string>>(
    type: TEventType,
    listener: (event: CustomEvent<TEventMap[TEventType]>) => void,
    options?: boolean | EventListenerOptions,
  ) {
    return super.removeEventListener(type, listener, options);
  }

  constructor(serializedLayerData: TRawDataType, parentComposition: Composition) {
    super();
    this._rawLayerData = serializedLayerData;
    this.parentComposition = parentComposition;
    this.videoDescriptor = parentComposition.videoDescriptor;

    this.uuid = this._rawLayerData.meta.uuid || '';
    this.layerType = this._rawLayerData.ty;

    // Ensure reference change handlers are bound to the class instance
    this.onImageContentReferenceChange = this.onImageContentReferenceChange?.bind(this);
    this.onTextContentReferenceChange = this.onTextContentReferenceChange?.bind(this);
    this.onFontReferenceChange = this.onFontReferenceChange?.bind(this);
    this.onFillColorReferenceChange = this.onFillColorReferenceChange?.bind(this);
    this.onStrokeColorReferenceChange = this.onStrokeColorReferenceChange?.bind(this);
    this.onFillEffectColorReferenceChange = this.onFillEffectColorReferenceChange?.bind(this);
    this.onGradientFillColorReferenceChange = this.onGradientFillColorReferenceChange?.bind(this);

    this.registerReferences();

    this.onVideoDescriptorChange = (evt: CustomEvent<ObservableObjectChange>) => {
      if (this.isDetached) {
        return;
      }

      const isDataValid = this.revalidateRawData();
      if (!isDataValid) {
        return;
      }

      const layerDataKeyPath = getObservableObjectKeyPath(this.rawLayerData);
      if (!layerDataKeyPath) {
        return false;
      }

      const matchingLayerDataChange = matchObservableObjectChange(evt.detail, layerDataKeyPath);
      if (matchingLayerDataChange) {
        const [changedKeyPath, prevValue, newValue] = matchingLayerDataChange;
        // Convert the changed key path to be relative to the root of the layer's data object
        // for more convenient matching.
        const relativeChangedKeyPath = getRelativeKeyPath(changedKeyPath, layerDataKeyPath);
        if (relativeChangedKeyPath !== null) {
          this.onLayerDataChange([relativeChangedKeyPath, prevValue, newValue]);
        }
        // Dispatch a generic "change" event when any part of the layer's data has changed.
        this.dispatchEvent('change', undefined);
      }
    };
    this.videoDescriptor.addEventListener('change', this.onVideoDescriptorChange);
  }

  /**
   * Checks that this.rawLayerData is still pointing to the latest correct object in the video descriptor.
   * Disconnections can occur if the video descriptor is mass updated via something like an autofill.
   *
   * Returns true if the layer data is valid or has been reconnected, false if it has been disconnected and should be cleaned up/ignored.
   */
  private revalidateRawData(): boolean {
    const layerDataKeyPath = getObservableObjectKeyPath(this.rawLayerData);
    if (!layerDataKeyPath) {
      return false;
    }

    let foundLayerData = get(this.videoDescriptor.rawData, layerDataKeyPath);

    if (this.videoDescriptor.rawData === foundLayerData) {
      // If the currently stored rawLayerData is the same as the video descriptor's raw data,
      // it means the layer data's reference is still valid and hasn't been moved or changed.
      return true;
    }

    if (foundLayerData && get(foundLayerData, 'meta.uuid') === this.uuid) {
      // If the found layer data has the same UUID as this layer, we just need to validate that it is still of the same type.
      if (get(foundLayerData, 'ty') === this.layerType) {
        // Re-assign to ensure we have the latest reference to the layer data
        this.rawLayerData = foundLayerData as TRawDataType;
        return true;
      } else {
        // The layer type has changed, which means this layer's data has been replaced with a different type
        // with the same uuid, meaning something like an image<>footage operation happened.
        // We should clean up this layer instance.
        this.cleanUp();
        return false;
      }
    }

    // The layer data has been potentially moved somewhere else on us.
    // Let's try to find it again in the project manifest.

    // Look in the root layers first.
    foundLayerData = this.videoDescriptor.rawData.projectManifest.layers.find(
      (layer): layer is TRawDataType =>
        layer?.meta?.uuid === this.uuid && layer?.ty === this.layerType,
    );
    if (foundLayerData) {
      this.rawLayerData = foundLayerData;
      return true;
    }

    // Search sub-comp layers next
    for (const asset of this.videoDescriptor.rawData.projectManifest.assets) {
      if (!('layers' in asset)) {
        continue;
      }

      foundLayerData = asset.layers.find(
        (layer): layer is TRawDataType =>
          layer.meta.uuid === this.uuid && layer.ty === this.layerType,
      );
      if (foundLayerData) {
        this.rawLayerData = foundLayerData;
        return true;
      }
    }

    // We struck out, looks like this layer is no longer in the video descriptor.
    // Let's clean up this layer instance.
    this.cleanUp();
    return false;
  }

  /**
   * Handler when the layer's data changes. We can use this to watch for changes which
   * should be reflected in the renderer/UI and trigger finer-grained events accordingly.
   *
   * @returns True if the change was handled, false if it was not.
   */
  onLayerDataChange(change: ObservableObjectChange): boolean {
    let didChange = false;

    if (matchObservableObjectChange(change, 'hd')) {
      // hd sets whether the layer is hidden or not
      this.dispatchEvent('change:visibility', undefined);
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'ef')) {
      // ef sets fill effect color
      this.dispatchEvent('change:fillEffectColor', undefined);
      didChange = true;
    }

    if (matchObservableObjectChange(change, 'ignoredReferences')) {
      this.dispatchEvent('change:ignoredReferences', undefined);
      didChange = true;
    }

    return didChange;
  }

  getName(): string {
    return this.rawLayerData.nm || '';
  }

  getUUID(): string {
    return this.uuid;
  }

  toString(): string {
    return `${this.constructor.name}<${this.getUUID()}>`;
  }

  toJSON(): TRawDataType {
    return this.rawLayerData;
  }

  getEditingAttributes(): LatestVDETypes.LayerExtendedAttributes | null {
    const templateManifest = this.videoDescriptor.rawData.templateManifest;
    if (!templateManifest.layersExtendedAttributes) {
      return null;
    }

    return templateManifest.layersExtendedAttributes[this.getUUID()];
  }

  getIsEditable(): boolean {
    return this.getEditingAttributes() !== null;
  }

  getCanEditFieldType(fieldType: keyof LatestVDETypes.LayerExtendedAttributes): boolean {
    const editingAttributes = this.getEditingAttributes();
    return editingAttributes?.[fieldType] !== undefined && editingAttributes?.[fieldType] !== null;
  }

  getIsHidden(): boolean {
    return this.rawLayerData.hd ?? false;
  }
  setIsHidden(isHidden: boolean, source: LatestVDETypes.HiddenBySource): void {
    this.rawLayerData.meta.hiddenBy = isHidden ? source : undefined;
    this.rawLayerData.hd = isHidden;
  }

  getHiddenBySource(): LatestVDETypes.HiddenBySource | undefined {
    return this.rawLayerData.meta.hiddenBy;
  }

  private cachedParentLayer: VideoDescriptorLayer | null | typeof NO_VALUE = NO_VALUE;
  /**
   * Get the linked parent layer for this layer, if one exists.
   * If a layer has a linked parent, it will be positioned relative to that parent layer's
   * position.
   */
  getParentLayer(): VideoDescriptorLayer | null {
    if (this.cachedParentLayer !== NO_VALUE) {
      return this.cachedParentLayer;
    }

    const parentIndex = this.rawLayerData.parent;
    if (typeof parentIndex !== 'number') {
      return null;
    }

    const parentLayer =
      this.parentComposition.layers.find((layer) => layer.rawLayerData.ind === parentIndex) ?? null;
    if (!parentLayer) {
      throw new Error(
        `Parent layer with index ${parentIndex} not found in composition ${this.parentComposition.toString()}`,
      );
    }
    this.cachedParentLayer = parentLayer;

    return parentLayer;
  }

  /**
   * Gets an array of objects representing the in and out points for each instance of the layer in frames.
   * A layer can be present in a composition which is referenced in multiple places in the video, hence why
   * we need an array instead of a single i/o point.
   *
   * Returns null if the layer is not present in any active compositions in the video.
   */
  getInAndOutPointFrames() {
    const inAndOutPointFrames = this.videoDescriptor.timeline.getLayerGlobalInOutPointFrames(
      this as VideoDescriptorLayer,
    );
    return inAndOutPointFrames;
  }

  getInAndOutPointSeconds() {
    const frameRate = this.videoDescriptor.getFramerate();
    return (
      this.getInAndOutPointFrames()?.map(({ inPoint, outPoint }) => ({
        inPoint: inPoint / frameRate,
        outPoint: outPoint / frameRate,
      })) ?? null
    );
  }

  /**
   * Gets the frame durations of every active instance of the layer.
   */
  getAllFrameDurations() {
    const inAndOutPoints = this.getInAndOutPointFrames();
    if (!inAndOutPoints || inAndOutPoints.length === 0) {
      return [];
    }

    return inAndOutPoints.map(({ inPoint, outPoint }) => outPoint - inPoint);
  }

  /**
   * Gets the longest frame duration of all active instances of the layer.
   */
  getLongestFrameDuration() {
    const allDurations = this.getAllFrameDurations();
    if (allDurations.length === 0) {
      return 0;
    }

    return Math.max(...allDurations);
  }

  getLongestSecondsDuration() {
    return this.getLongestFrameDuration() / this.videoDescriptor.getFramerate();
  }

  private cachedTemplateManifestDisplayFrame: number | null | typeof NO_VALUE = NO_VALUE;
  /**
   * Gets the ideal display frame for this layer as defined in the template manifest, which was set manually in studio;
   * there is a chance that a layer may not have an officially defined display frame like that, in which case this will return null.
   */
  getTemplateManifestDisplayFrame(): number | null {
    if (this.cachedTemplateManifestDisplayFrame !== NO_VALUE) {
      return this.cachedTemplateManifestDisplayFrame;
    }

    const editingAttributes = this.getEditingAttributes();
    if (
      editingAttributes &&
      'content' in editingAttributes &&
      editingAttributes.content !== undefined &&
      editingAttributes.content !== null
    ) {
      // If it has an override, use the override's ideal display time
      if ('override' in editingAttributes.content) {
        if (this.textContentReference) {
          this.cachedTemplateManifestDisplayFrame =
            this.textContentReference.rawReferenceData.frameNumber;
          return this.cachedTemplateManifestDisplayFrame;
        } else if (this.imageContentReference) {
          this.cachedTemplateManifestDisplayFrame =
            this.imageContentReference.rawReferenceData.frameNumber;
          return this.cachedTemplateManifestDisplayFrame;
        }
      } else {
        // Otherwise, use the frame number from the template manifest if it isn't null
        if (editingAttributes.content.frameNumber !== null) {
          this.cachedTemplateManifestDisplayFrame = editingAttributes.content.frameNumber;
          return this.cachedTemplateManifestDisplayFrame;
        }
      }
    }

    this.cachedTemplateManifestDisplayFrame = null;
    return this.cachedTemplateManifestDisplayFrame;
  }

  private cachedIdealDisplayFrame: number | typeof NO_VALUE = NO_VALUE;
  getIdealDisplayFrame(): number {
    if (this.cachedIdealDisplayFrame !== NO_VALUE) {
      return this.cachedIdealDisplayFrame;
    }

    const templateManifestDisplayFrame = this.getTemplateManifestDisplayFrame();
    if (templateManifestDisplayFrame !== null) {
      this.cachedIdealDisplayFrame = templateManifestDisplayFrame;
      return templateManifestDisplayFrame;
    }

    if (this.cachedIdealDisplayFrame === NO_VALUE) {
      // Fall back to the layer's in point if the template manifest doesn't specify a frame number
      const inAndOutPoints = this.getInAndOutPointFrames();
      if (inAndOutPoints[0]) {
        const { inPoint, outPoint } = inAndOutPoints[0];
        this.cachedIdealDisplayFrame = (inPoint + outPoint) / 2;
      } else {
        console.error('No ideal display frame found for layer', this);
        this.cachedIdealDisplayFrame = 0;
      }
    }
    return this.cachedIdealDisplayFrame;
  }

  getIdealDisplayTime(): number {
    return this.getIdealDisplayFrame() / this.videoDescriptor.getFramerate();
  }

  /** ---- REFERENCES ---- */

  updateIgnoredReferences(
    ignoredReferenceUpdates: Partial<LatestVDETypes.BaseLayer['ignoredReferences']>,
  ): void {
    this.rawLayerData.ignoredReferences ??= {};
    Object.assign(this.rawLayerData.ignoredReferences, ignoredReferenceUpdates);
  }

  getIsReferenceIgnored(referenceType: `${LatestVDETypes.ReferenceType}`): boolean {
    return this.rawLayerData.ignoredReferences?.[referenceType] ?? false;
  }

  /**
   * Goes through the layer's REFERENCES and gathers all available reference instances for the layer.
   */
  registerReferences() {
    const editingAttributes = this.getEditingAttributes();
    if (!editingAttributes) {
      return;
    }

    for (const attributeName in editingAttributes) {
      switch (attributeName as keyof LatestVDETypes.LayerExtendedAttributes) {
        case 'content': {
          const contentAttributes = editingAttributes.content;
          if (
            !contentAttributes ||
            !('override' in contentAttributes) ||
            !contentAttributes.override
          ) {
            // If the content override is not set, we don't need to do anything
            break;
          }

          // A "content" override can either be an image or text reference, depending on the layer type
          const imageContentReference =
            this.videoDescriptor.references.image[contentAttributes.override];

          if (imageContentReference) {
            this.setImageContentReference(imageContentReference);
          } else {
            const textContentReference =
              this.videoDescriptor.references.text[contentAttributes.override];
            if (textContentReference) {
              this.setTextContentReference(textContentReference);
            } else {
              throw new Error(
                `${this.toString()}: Unable to find valid TextReference or ImageReference for content override id "${
                  contentAttributes.override
                }"`,
              );
            }
          }
          break;
        }
        case 'font': {
          const fontAttributes = editingAttributes.font;
          if (!fontAttributes?.override) {
            // If the font override is not set, we don't need to do anything
            break;
          }

          const fontReference = this.videoDescriptor.references.font[fontAttributes.override];
          if (!fontReference) {
            throw new Error(
              `${this.toString()}: Unable to find valid FontReference for font override id "${
                fontAttributes.override
              }"`,
            );
          }

          this.setFontReference(fontReference);
          break;
        }
        case 'fillColor': {
          const fillColorAttributes = editingAttributes.fillColor;
          if (
            !fillColorAttributes ||
            typeof fillColorAttributes !== 'object' ||
            !fillColorAttributes.override
          ) {
            // If the fill color override is not set, we don't need to do anything
            break;
          }

          const colorReference =
            this.videoDescriptor.references.color[fillColorAttributes.override];
          if (!colorReference) {
            throw new Error(
              `${this.toString()}: Unable to find valid ColorReference for fill color reference id "${
                fillColorAttributes.override
              }"`,
            );
          }

          this.setFillColorReference(colorReference);
          break;
        }
        case 'strokeColor': {
          const strokeColorAttributes = editingAttributes.strokeColor;
          if (
            !strokeColorAttributes ||
            typeof strokeColorAttributes !== 'object' ||
            !strokeColorAttributes.override
          ) {
            // If the stroke color override is not set, we don't need to do anything
            break;
          }

          const colorReference =
            this.videoDescriptor.references.color[strokeColorAttributes.override];
          if (!colorReference) {
            throw new Error(
              `${this.toString()}: Unable to find valid ColorReference for stroke color reference id "${
                strokeColorAttributes.override
              }"`,
            );
          }

          this.setStrokeColorReference(colorReference);
          break;
        }
        case 'fillEffect': {
          const fillEffectAttributes = editingAttributes.fillEffect;
          if (
            !fillEffectAttributes ||
            typeof fillEffectAttributes !== 'object' ||
            !fillEffectAttributes.override
          ) {
            break;
          }

          const colorReference =
            this.videoDescriptor.references.color[fillEffectAttributes.override];
          if (!colorReference) {
            throw new Error(
              `${this.toString()}: Unable to find valid ColorReference for fill effect reference id "${
                fillEffectAttributes.override
              }"`,
            );
          }

          this.setFillEffectColorReference(colorReference);
          break;
        }
        case 'gradientFill': {
          const gradientFillAttributes = editingAttributes.gradientFill;
          if (!gradientFillAttributes) {
            // If there aren't any gradient fill attributes, we don't need to do anything
            break;
          }

          for (
            let gradientStepIndex = 0, gradientStepCount = gradientFillAttributes.length;
            gradientStepIndex < gradientStepCount;
            gradientStepIndex += 1
          ) {
            const gradientFillAttribute = gradientFillAttributes[gradientStepIndex];
            if (
              !gradientFillAttribute ||
              typeof gradientFillAttribute !== 'object' ||
              !gradientFillAttribute.override
            ) {
              continue;
            }

            const colorReference =
              this.videoDescriptor.references.color[gradientFillAttribute.override];
            if (!colorReference) {
              throw new Error(
                `${this.toString()}: Unable to find valid ColorReference for gradient fill reference id "${
                  gradientFillAttribute.override
                }"`,
              );
            }

            this.setGradientFillColorReference(colorReference, gradientStepIndex);
          }
          break;
        }
      }
    }
  }

  setTextContentReference(newTextContentReference: TextReference): void {
    if (!this.onTextContentReferenceChange) {
      throw new Error(
        `${this.toString()}: Cannot set text content reference without a change handler defined.`,
      );
    }

    const oldTextContentReference = this.textContentReference;
    if (oldTextContentReference) {
      oldTextContentReference.removeEventListener(
        'change:value',
        this.onTextContentReferenceChange,
      );
    }

    // Update the template manifest with the new content reference
    const templateManifest = this.videoDescriptor.rawData.templateManifest;
    templateManifest.layersExtendedAttributes ??= {};
    const layerUUID = this.getUUID();
    templateManifest.layersExtendedAttributes[layerUUID] ??= {};
    templateManifest.layersExtendedAttributes[layerUUID].content = {
      override: newTextContentReference.getID(),
    };

    this.textContentReference = newTextContentReference;
    this.textContentReference.addEventListener('change:value', this.onTextContentReferenceChange);

    // Trigger the change handler manually to get the layer synced up with the new reference value
    this.onTextContentReferenceChange();
  }

  setImageContentReference(newImageContentReference: ImageReference): void {
    if (!this.onImageContentReferenceChange) {
      throw new Error(
        `${this.toString()}: Cannot set image content reference without a change handler defined.`,
      );
    }

    const oldImageContentReference = this.imageContentReference;
    if (oldImageContentReference) {
      oldImageContentReference.removeEventListener(
        'change:value',
        this.onImageContentReferenceChange,
      );
    }

    // Update the template manifest with the new content reference
    const templateManifest = this.videoDescriptor.rawData.templateManifest;
    templateManifest.layersExtendedAttributes ??= {};
    const layerUUID = this.getUUID();
    templateManifest.layersExtendedAttributes[layerUUID] ??= {};
    templateManifest.layersExtendedAttributes[layerUUID].content = {
      override: newImageContentReference.getID(),
    };

    this.imageContentReference = newImageContentReference;
    this.imageContentReference.addEventListener('change:value', this.onImageContentReferenceChange);

    // Trigger the change handler manually to get the layer synced up with the new reference value
    this.onImageContentReferenceChange();
  }

  setFontReference(newFontReference: FontReference): void {
    if (!this.onFontReferenceChange) {
      throw new Error(
        `${this.toString()}: Cannot set font reference without a change handler defined.`,
      );
    }

    const oldFontReference = this.fontReference;
    if (oldFontReference) {
      oldFontReference.removeEventListener('change:value', this.onFontReferenceChange);
    }

    // Update the template manifest with the new fill color reference
    const templateManifest = this.videoDescriptor.rawData.templateManifest;
    templateManifest.layersExtendedAttributes ??= {};
    const layerUUID = this.getUUID();
    templateManifest.layersExtendedAttributes[layerUUID] ??= {};
    templateManifest.layersExtendedAttributes[layerUUID].font = {
      override: newFontReference.getID(),
    };

    this.fontReference = newFontReference;
    this.fontReference.addEventListener('change:value', this.onFontReferenceChange);

    // Trigger the change handler manually to get the layer synced up with the new reference value
    this.onFontReferenceChange();
  }

  /**
   * Updates the fill color reference for this layer.
   */
  setFillColorReference(newFillColorReference: ColorReference): void {
    if (!this.onFillColorReferenceChange) {
      throw new Error(
        `${this.toString()}: Cannot set fill color reference without a change handler defined.`,
      );
    }

    const oldFillColorReference = this.fillColorReference;
    if (oldFillColorReference) {
      oldFillColorReference.removeEventListener('change:value', this.onFillColorReferenceChange);
    }

    // Update the template manifest with the new fill color reference
    const templateManifest = this.videoDescriptor.rawData.templateManifest;
    templateManifest.layersExtendedAttributes ??= {};
    const layerUUID = this.getUUID();
    templateManifest.layersExtendedAttributes[layerUUID] ??= {};
    templateManifest.layersExtendedAttributes[layerUUID].fillColor = {
      override: newFillColorReference.getID(),
    };

    this.fillColorReference = newFillColorReference;
    this.fillColorReference.addEventListener('change:value', this.onFillColorReferenceChange);

    // Trigger the change handler manually to get the layer synced up with the new reference value
    this.onFillColorReferenceChange();
  }

  setStrokeColorReference(newStrokeColorReference: ColorReference): void {
    if (!this.onStrokeColorReferenceChange) {
      throw new Error(
        `${this.toString()}: Cannot set stroke color reference without a change handler defined.`,
      );
    }

    const oldStrokeColorReference = this.strokeColorReference;
    if (oldStrokeColorReference) {
      oldStrokeColorReference.removeEventListener(
        'change:value',
        this.onStrokeColorReferenceChange,
      );
    }

    // Update the template manifest with the new stroke color reference
    const templateManifest = this.videoDescriptor.rawData.templateManifest;
    templateManifest.layersExtendedAttributes ??= {};
    const layerUUID = this.getUUID();
    templateManifest.layersExtendedAttributes[layerUUID] ??= {};
    templateManifest.layersExtendedAttributes[layerUUID].strokeColor = {
      override: newStrokeColorReference.getID(),
    };

    this.strokeColorReference = newStrokeColorReference;
    this.strokeColorReference.addEventListener('change:value', this.onStrokeColorReferenceChange);

    // Trigger the change handler manually to get the layer synced up with the new reference value
    this.onStrokeColorReferenceChange();
  }

  setFillEffectColorReference(newFillEffectColorReference: ColorReference): void {
    if (!this.onFillEffectColorReferenceChange) {
      throw new Error(
        `${this.toString()}: Cannot set fill effect color reference without a change handler defined.`,
      );
    }

    const oldFillEffectColorReference = this.fillEffectColorReference;
    if (oldFillEffectColorReference) {
      oldFillEffectColorReference.removeEventListener(
        'change:value',
        this.onFillEffectColorReferenceChange,
      );
    }

    // Update the template manifest with the new fill effect color reference
    const templateManifest = this.videoDescriptor.rawData.templateManifest;
    templateManifest.layersExtendedAttributes ??= {};
    const layerUUID = this.getUUID();
    templateManifest.layersExtendedAttributes[layerUUID] ??= {};
    templateManifest.layersExtendedAttributes[layerUUID].fillEffect = {
      override: newFillEffectColorReference.getID(),
    };

    this.fillEffectColorReference = newFillEffectColorReference;
    this.fillEffectColorReference.addEventListener(
      'change:value',
      this.onFillEffectColorReferenceChange,
    );

    // Trigger the change handler manually to get the layer synced up with the new reference value
    this.onFillEffectColorReferenceChange();
  }

  setGradientFillColorReference(
    newGradientFillColorReference: ColorReference,
    gradientStepIndex: number,
  ): void {
    if (!this.onGradientFillColorReferenceChange) {
      throw new Error(
        `${this.toString()}: Cannot set gradient fill color reference without a change handler defined.`,
      );
    }

    const oldGradientFillColorReference = this.gradientFillColorReferences?.[gradientStepIndex];
    if (oldGradientFillColorReference && this.gradientFillColorReferenceChangeListeners) {
      oldGradientFillColorReference.removeEventListener(
        'change:value',
        this.gradientFillColorReferenceChangeListeners[gradientStepIndex],
      );
    }

    // Update the template manifest with the new gradient fill color reference
    const templateManifest = this.videoDescriptor.rawData.templateManifest;
    templateManifest.layersExtendedAttributes ??= {};
    const layerUUID = this.getUUID();
    templateManifest.layersExtendedAttributes[layerUUID] ??= {};
    templateManifest.layersExtendedAttributes[layerUUID].gradientFill ??= [];
    const gradientFillAttributes =
      templateManifest.layersExtendedAttributes[layerUUID].gradientFill;
    if (gradientFillAttributes) {
      gradientFillAttributes[gradientStepIndex] = {
        override: newGradientFillColorReference.getID(),
      };
    }

    this.gradientFillColorReferences ??= [];
    this.gradientFillColorReferences[gradientStepIndex] = newGradientFillColorReference;

    const onGradientFillColorReferenceChangeListener = () => {
      this.onGradientFillColorReferenceChange?.(gradientStepIndex);
    };

    this.gradientFillColorReferenceChangeListeners ??= [];
    this.gradientFillColorReferenceChangeListeners[gradientStepIndex] =
      onGradientFillColorReferenceChangeListener;

    newGradientFillColorReference.addEventListener(
      'change:value',
      onGradientFillColorReferenceChangeListener,
    );

    onGradientFillColorReferenceChangeListener();
  }

  /** ---- FILL EFFECT; effects can theoretically be applied to all layer types ---- */

  getFillEffectColor<TColorFormat extends 'hex' | 'rgb' = 'hex'>(format = 'hex' as TColorFormat) {
    const layerEffects = this.rawLayerData.ef;
    if (!layerEffects) {
      return null;
    }

    const fillEffectColorValue = layerEffects
      .find((effect) => effect.ty === LatestVDETypes.EffectType.Fill)
      ?.ef.find((effectControl) => effectControl.nm === 'Color')?.v;
    if (!fillEffectColorValue) {
      return null;
    }

    let colorRGBTuple: [number, number, number] | null = null;

    if (fillEffectColorValue.a === 1 && 's' in fillEffectColorValue.k[0]) {
      const colorValue = fillEffectColorValue.k[0].s;
      if (Array.isArray(colorValue)) {
        colorRGBTuple = colorValue as [number, number, number];
      }
    } else {
      const colorValue = fillEffectColorValue.k;
      if (Array.isArray(colorValue)) {
        colorRGBTuple = colorValue as [number, number, number];
      }
    }

    return colorRGBTuple ? getFormattedColor(colorRGBTuple, format) : null;
  }

  private updateFillEffectColor(newColor: [number, number, number]): void {
    const layerEffects = this.rawLayerData.ef;
    if (!layerEffects) {
      return;
    }

    const updatedLayerEffects = cloneDeep(layerEffects);

    for (const effect of updatedLayerEffects) {
      if (effect.ty === LatestVDETypes.EffectType.Fill) {
        for (const effectControl of effect.ef) {
          if (effectControl.nm === 'Color') {
            if (effectControl.v.a === 1 && 's' in effectControl.v.k[0]) {
              effectControl.v.k[0].s = newColor;
            } else {
              effectControl.v.k = newColor;
            }
          }
        }
      }
    }

    this.rawLayerData.ef = updatedLayerEffects;
  }

  setFillEffectColor(
    newColor: string | [number, number, number],
    shouldOverrideReference = false,
  ): void {
    if (!this.getCanEditFieldType('fillEffect')) {
      throw new Error(`${this.toString()}: Fill effect color is not editable for this layer.`);
    }

    if (shouldOverrideReference) {
      this.updateIgnoredReferences({
        fillEffect: true,
      });
    }

    if (this.fillEffectColorReference && !this.getIsReferenceIgnored('fillEffect')) {
      this.fillEffectColorReference.setColor(getFormattedColor(newColor, 'hex'));
    } else {
      this.updateFillEffectColor(getFormattedColor(newColor, 'rgb'));
    }
  }

  /**
   * Event listener for when the fill effect color reference changes.
   */
  onFillEffectColorReferenceChange(): void {
    if (!this.fillEffectColorReference || this.getIsReferenceIgnored('fillEffect')) {
      return;
    }

    const newColorHex = this.fillEffectColorReference.getColor();
    this.updateFillEffectColor(hexStringToRGBArray(newColorHex));
  }

  /**
   * Method called when the layer is removed from the video descriptor.
   * Layers should extend this method to perform any necessary clean up,
   * ie removing assets from the video descriptor.
   */
  cleanUp(): void {
    this.isDetached = true;
    this.videoDescriptor.removeEventListener('change', this.onVideoDescriptorChange);
    const parentCompositionLayerIndex = this.parentComposition.layers.indexOf(
      this as VideoDescriptorLayer,
    );
    if (parentCompositionLayerIndex >= 0) {
      // Remove this layer from the parent composition's layers if it is still present
      this.parentComposition.layers.splice(parentCompositionLayerIndex, 1);
      this.parentComposition.dispatchEvent('layerRemoved', this as VideoDescriptorLayer);
    }
    this.dispatchEvent('removed', undefined);
  }
}
